if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}


/* ============================================================
   VF / Pebble & Wick — Cinematic Product Form Enhancements
   Safe layer on top of Dawn product-form.js.
   Keeps add-to-cart AJAX, StandardEvents and cart rendering intact.
   ============================================================ */

(() => {
  const ProductFormElement = customElements.get('product-form');
  if (!ProductFormElement || ProductFormElement.prototype.__vfCinematicPatched) return;

  ProductFormElement.prototype.__vfCinematicPatched = true;

  const originalOnSubmitHandler = ProductFormElement.prototype.onSubmitHandler;
  const originalHandleErrorMessage = ProductFormElement.prototype.handleErrorMessage;
  const originalToggleSubmitButton = ProductFormElement.prototype.toggleSubmitButton;

  const ensureStatusRegion = (productForm) => {
    let status = productForm.querySelector('[data-vf-product-form-status]');
    if (status) return status;

    status = document.createElement('div');
    status.setAttribute('data-vf-product-form-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.className = 'visually-hidden vf-product-form__status';

    productForm.appendChild(status);
    return status;
  };

  const getSelectedVariantId = (productForm) => productForm.variantIdInput?.value || null;

  const setProductFormState = (productForm, state, detail = {}) => {
    productForm.dataset.vfState = state;
    productForm.classList.toggle('vf-product-form-loading', state === 'loading');
    productForm.classList.toggle('vf-product-form-success', state === 'success');
    productForm.classList.toggle('vf-product-form-error', state === 'error');

    productForm.dispatchEvent(
      new CustomEvent(`vf:product-form-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const pulseSubmit = (productForm) => {
    const button = productForm.submitButton;
    if (!button) return;

    button.classList.remove('vf-product-form-pulse');
    void button.offsetWidth;
    button.classList.add('vf-product-form-pulse');

    window.setTimeout(() => {
      button.classList.remove('vf-product-form-pulse');
    }, 720);
  };

  ProductFormElement.prototype.onSubmitHandler = function vfOnSubmitHandler(event) {
    const variantId = getSelectedVariantId(this);
    const quantity = parseInt(new FormData(this.form).get('quantity'), 10) || 1;
    const status = ensureStatusRegion(this);

    status.textContent = '';
    setProductFormState(this, 'loading', { variantId, quantity });

    const buttonObserver = new MutationObserver(() => {
      const isLoading = this.submitButton?.classList.contains('loading');
      const isDisabled = this.submitButton?.getAttribute('aria-disabled') === 'true';

      if (isLoading || isDisabled) return;

      buttonObserver.disconnect();

      if (this.error) {
        setProductFormState(this, 'error', { variantId, quantity });
        return;
      }

      status.textContent = window.variantStrings?.addToCart
        ? `${window.variantStrings.addToCart} complete.`
        : 'Added to cart.';

      setProductFormState(this, 'success', { variantId, quantity });
      pulseSubmit(this);

      document.documentElement.classList.add('vf-product-added');
      window.setTimeout(() => {
        this.classList.remove('vf-product-form-success');
        document.documentElement.classList.remove('vf-product-added');
      }, 1200);
    });

    if (this.submitButton) {
      buttonObserver.observe(this.submitButton, {
        attributes: true,
        attributeFilter: ['class', 'aria-disabled', 'disabled']
      });
    }

    const result = originalOnSubmitHandler.call(this, event);

    window.setTimeout(() => {
      if (this.submitButton?.classList.contains('loading')) return;
      buttonObserver.disconnect();
    }, 12000);

    return result;
  };

  ProductFormElement.prototype.handleErrorMessage = function vfHandleErrorMessage(errorMessage = false) {
    const result = originalHandleErrorMessage.call(this, errorMessage);

    if (errorMessage) {
      const status = ensureStatusRegion(this);
      status.textContent = errorMessage;
      setProductFormState(this, 'error', {
        variantId: getSelectedVariantId(this),
        message: errorMessage
      });
    } else {
      this.classList.remove('vf-product-form-error');
    }

    return result;
  };

  ProductFormElement.prototype.toggleSubmitButton = function vfToggleSubmitButton(disable = true, text) {
    const result = originalToggleSubmitButton.call(this, disable, text);

    this.classList.toggle('vf-product-form-disabled', Boolean(disable));
    this.submitButton?.classList.toggle('vf-product-form-button-disabled', Boolean(disable));

    return result;
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('product-form').forEach((productForm) => {
      productForm.classList.add('vf-product-form-enhanced');
      ensureStatusRegion(productForm);
    });
  }, { once: true });

  document.addEventListener('change', (event) => {
    const form = event.target.closest('product-form');
    if (!form) return;

    form.classList.add('vf-product-form-selection-changed');
    window.setTimeout(() => {
      form.classList.remove('vf-product-form-selection-changed');
    }, 500);
  });
})();
